import QRCode from 'qrcode';
import { ticketService } from '@/services/ticket-service';
import { navigate } from '@/utils/navigate';
import './nu-user-menu';
import { applyDevWatermark } from '@/services/dev-watermark';
import { getAppConfig } from '@/services/app-config';
const COUNT_OPTIONS = [5, 10, 25, 50];

class NuTokenProvision extends HTMLElement {
  private readonly root: ShadowRoot;
  private _tokens: string[] = [];
  private _qrSvgs: string[] = [];
  private _loading = false;
  private _error = '';
  private _small = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id']; }
  get orgId() { return this.getAttribute('org-id') ?? ''; }

  connectedCallback() {
    this._render();
    applyDevWatermark(this.root);
  }

  private _render() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>Subscription QR Codes</span>
          <div class="toolbar-actions">
            ${this._tokens.length > 0 ? `
              <label class="size-label">Size:
                <select class="size-select" id="qr-size">
                  <option value="large">2" × 2"</option>
                  <option value="small">1" × 1"</option>
                </select>
              </label>
              <button class="btn-primary" id="print-btn">Print</button>
            ` : ''}
            <button class="btn-back" id="back-btn">← Back</button>
            <nu-user-menu></nu-user-menu>
          </div>
        </header>

        <div class="body">
          <div class="controls">
            <p class="hint mono">
              Generate QR code stickers. Attach one to each ticket — customers scan it to subscribe for pickup notifications.
            </p>
            <div class="count-row">
              <label class="eyebrow mono" for="count-select">How many?</label>
              <select class="count-select" id="count-select">
                ${COUNT_OPTIONS.map(n => `<option value="${n}">${n}</option>`).join('')}
              </select>
              <button class="btn-generate" id="generate-btn" ${this._loading ? 'disabled' : ''}>
                ${this._loading ? '<span class="spinner-sm"></span> Generating…' : 'Generate'}
              </button>
            </div>
            ${this._error ? `<p class="field-error">${this._error}</p>` : ''}
          </div>

          ${this._tokens.length > 0 ? `
            <p class="generated-count mono">${this._tokens.length} token${this._tokens.length !== 1 ? 's' : ''} generated</p>
            <div class="qr-grid" id="qr-grid">
              ${this._qrSvgs.map((svg, i) => `
                <div class="qr-item">
                  <div class="qr-code">${svg}</div>
                  <div class="qr-brand">notify-u <span class="dot"></span></div>
                  <div class="qr-token mono">${this._tokens[i].slice(0, 8)}…</div>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </div>
      </div>
    `;

    this.root.querySelector('#back-btn')!.addEventListener('click', () => {
      navigate(`/app/org/${this.orgId}`);
    });

    this.root.querySelector('#generate-btn')?.addEventListener('click', () => this._generate());

    this.root.querySelector('#print-btn')?.addEventListener('click', () => window.print());

    this.root.querySelector('#qr-size')?.addEventListener('change', (e) => {
      this._small = (e.target as HTMLSelectElement).value === 'small';
      this.root.querySelector('#qr-grid')?.classList.toggle('qr-grid--small', this._small);
    });
  }

  private async _generate() {
    const countEl = this.root.querySelector<HTMLSelectElement>('#count-select');
    const count = parseInt(countEl?.value ?? '10', 10);

    this._loading = true;
    this._error = '';
    this._render();

    try {
      const [tokens, { appBaseUrl }] = await Promise.all([
        ticketService.provisionTokens(this.orgId, count),
        getAppConfig(),
      ]);
      const svgs = await Promise.all(
        tokens.map(t => QRCode.toString(`${appBaseUrl}/app/subscribe/${t}`, { type: 'svg', margin: 1 }))
      );
      this._tokens = tokens;
      this._qrSvgs = svgs;
    } catch (_err) {
      this._error = 'Failed to generate tokens. Please try again.';
    }

    this._loading = false;
    this._render();
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper-light, #FAF7F0);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      .top-bar {
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 12px;
        padding: 12px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(250,247,240,0.95);
        backdrop-filter: blur(6px);
        position: sticky; top: 0; z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 18px; letter-spacing: -0.02em;
        display: flex; align-items: center; gap: 7px;
      }
      .dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block; flex-shrink: 0;
      }
      .toolbar-actions { display: flex; align-items: center; gap: 10px; }
      .size-label {
        display: flex; align-items: center; gap: 6px;
        font-size: 13px; color: var(--ink-soft, #5A5346);
      }
      .size-select, .count-select {
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 4px; padding: 6px 10px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink, #2A2622); cursor: pointer;
      }

      .body {
        max-width: 860px;
        margin: 0 auto;
        padding: 28px 20px;
      }
      .controls {
        display: flex; flex-direction: column; gap: 14px;
        margin-bottom: 28px;
      }
      .hint {
        margin: 0;
        font-size: 12px;
        color: var(--ink-soft, #5A5346);
        letter-spacing: .03em;
        max-width: 56ch;
      }
      .count-row {
        display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
      }
      .eyebrow {
        font-size: 11px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--kiln-dark, #A15F1F); margin: 0;
      }
      .field-error {
        font-size: 12px; color: #b94a2c;
        font-family: 'IBM Plex Mono', monospace; margin: 0;
      }
      .generated-count {
        font-size: 12px; color: var(--ink-soft, #5A5346);
        margin: 0 0 16px; letter-spacing: .03em;
      }

      .btn-generate {
        display: flex; align-items: center; gap: 8px;
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 4px;
        padding: 8px 18px;
        font-family: 'Inter', sans-serif; font-weight: 600; font-size: 13px;
        cursor: pointer; transition: background .15s;
      }
      .btn-generate:hover:not(:disabled) { background: var(--kiln-dark, #A15F1F); }
      .btn-generate:disabled { opacity: 0.6; cursor: not-allowed; }
      .btn-primary {
        background: var(--kiln, #C97A2E); color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 4px; padding: 6px 14px;
        font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
        cursor: pointer; transition: background .15s;
      }
      .btn-primary:hover { background: var(--kiln-dark, #A15F1F); }
      .btn-back {
        background: transparent; border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px; padding: 5px 12px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink-soft, #5A5346); cursor: pointer; transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }

      .qr-grid {
        display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start;
      }
      .qr-item {
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        padding: 14px;
        background: white;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        break-inside: avoid; page-break-inside: avoid;
      }
      .qr-code svg { width: 192px; height: 192px; display: block; }
      .qr-grid--small .qr-code svg { width: 96px; height: 96px; }
      .qr-brand {
        font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700;
        color: var(--ink, #2A2622);
        display: flex; align-items: center; justify-content: center; gap: 5px;
      }
      .qr-token {
        font-size: 10px; color: var(--ink-soft, #5A5346); letter-spacing: .03em;
      }
      .qr-grid--small .qr-token { display: none; }

      .spinner-sm {
        width: 14px; height: 14px;
        border: 2px solid rgba(250,247,240,.4);
        border-top-color: var(--paper-light, #FAF7F0);
        border-radius: 50%;
        animation: spin .7s linear infinite;
        flex-shrink: 0;
      }
      @keyframes spin { to { transform: rotate(360deg); } }

      @media print {
        .top-bar { display: none !important; }
        .controls { display: none !important; }
        .generated-count { display: none !important; }
        .qr-grid { padding: 0; gap: 8px; }
        .qr-item { border: 1px solid #ccc; }
        .qr-code svg { width: 2in !important; height: 2in !important; }
        .qr-grid--small .qr-code svg { width: 1in !important; height: 1in !important; }
      }
    </style>`;
  }
}

customElements.define('nu-token-provision', NuTokenProvision);